import { useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, ArrowLeft, ChevronLeft, ChevronRight, FileText, PenLine, RotateCcw } from "lucide-react";
import { Badge, Button, Card, Textarea } from "@/design-system/components/ui";
import { cn } from "@/design-system/lib/utils";
import { PageHeader } from "@/components/layout/PageHeader";
import { useBreadcrumbs } from "@/components/layout/BreadcrumbsContext";
import { BrokerRecordLink, PortalAccountLink } from "@/components/crosslinks/BrokerCrossLinks";
import { ResourceDrawer } from "@/components/portfolioAdmin/loanDetail/common/ResourceDrawer";
import { DocumentComparisonPanel, DocumentPreviewPane, type PreviewDocument } from "@/components/review/DocumentPreview";
import { FolderPicker as SharedFolderPicker } from "@/components/dms/FolderPicker";
import { ScanRecoveryControls } from "@/components/review/ScanRecoveryControls";
import { ConfirmModal } from "@/components/ui/PromptModals";
import { useToast } from "@/components/ui/Toast";
import { Modal } from "@/components/ui/Modal";
import { useAuth } from "@/hooks/useAuth";
import { useDateFormatter } from "@/utils/datetime";
import { extractErrorMessage, ApiError } from "@/api/client";
import { isSessionExpired, retryUnlessSessionExpired } from "@/api/polling";
import { useAssignableUsers } from "@/hooks/useAssignableUsers";
import { useInternalUserLabels, fallbackUserLabel } from "@/api/internalUsers";
import {
  dealStagingApi,
  type DealStagingDetail,
  type DealStagingSection,
  type DealStagingDocument,
  type DealStagingReviewItem,
  type DealStagingReturnComment,
  type DealSubmissionResponse,
  type DealSubmissionEventType,
  type DealSubmissionSectionReviewStatus,
  type ScanStatus,
  type DealSubmissionStatus,
  type DealSubmissionOrigin,
  type DealImportBlockReason,
} from "@/api/dealStaging";
import { parseFormSchema, type FormField, type FormSchema } from "@/lib/formSchema";
import { invalidateAfterImport, stagingKeys } from "./stagingKeys";
import {
  stagingCopyFor,
  isEscalation,
  liaisonSuppliedField,
  type LiaisonSuppliedField,
  type StagingOriginCopy,
} from "./stagingOriginCopy";
import { normalizeDealSubmissionFinancialSchema } from "@/lib/dealSubmissionFinancialLabels";
import { PortalFormRenderer, findFirstFormatError, type PortalFormData } from "@/portal/components/PortalFormRenderer";

function StatusBadge({ status }: { status: DealSubmissionStatus }) {
  switch (status) {
    case "Submitted": return <Badge status="info">Submitted</Badge>;
    case "InStaging": return <Badge status="review">In staging</Badge>;
    case "ReturnedForChanges": return <Badge status="info">Returned</Badge>;
    case "Approved": return <Badge status="finalized">Approved</Badge>;
    case "Imported": return <Badge status="finalized">Imported</Badge>;
    case "Declined": return <Badge status="archived">Declined</Badge>;
    default: return <Badge status="info">{status}</Badge>;
  }
}

function SectionReviewBadge({ status }: { status: DealSubmissionSectionReviewStatus }) {
  if (status === "Approved") return <Badge status="finalized">Approved</Badge>;
  if (status === "Returned") return <Badge status="archived">Returned</Badge>;
  return <Badge status="info">Pending</Badge>;
}

function ScanBadge({ status }: { status: ScanStatus }) {
  switch (status) {
    case "Clean": return <Badge status="finalized">Clean</Badge>;
    case "Infected": return <Badge status="archived">Infected</Badge>;
    case "Error": return <Badge status="info">Scan error</Badge>;
    default: return <Badge status="info">Scanning…</Badge>;
  }
}

// --- Pure helpers (exported for tests) ---------------------------------------

/** An item that needs a reviewer's attention: returned, AI assessment failed,
 *  or the upload's scan came back infected/errored. */
export function itemFlagged(i: DealStagingReviewItem): boolean {
  return (
    i.reviewStatus === "Returned" ||
    i.assessmentStatus === "Failed" ||
    i.scanStatus === "Infected" ||
    i.scanStatus === "Error"
  );
}

/** A document whose antivirus scan hasn't completed yet. */
export function itemScanning(i: DealStagingReviewItem): boolean {
  return i.kind === "Document" && i.scanStatus === "Pending";
}

/** Short type tag for a row: "Form", or a file extension like "PDF". */
export function fileTypeTag(i: DealStagingReviewItem): string {
  if (i.kind === "Form") return "Form";
  const name = i.originalFileName ?? "";
  const ext = name.includes(".") ? name.split(".").pop() : "";
  return ext ? ext.toUpperCase() : "FILE";
}

/** Resolve which form/document a return comment is about, plus the review
 *  item id used to open that item's drawer. The SectionId / DocumentRequestId
 *  association is recorded at return time; this surfaces it as a label + link
 *  so a reviewer (and broker) can see exactly what each note targets. */
export function commentItemRef(
  submission: DealStagingDetail,
  comment: DealStagingReturnComment,
): { label: string; itemId: string } | null {
  // Section-first, mirroring the backend's ItemLabel resolution
  // (PortalDealSubmissionStatusService) so both surfaces agree. A per-item
  // return ever sets exactly one of the two ids, so this only matters
  // defensively.
  if (comment.sectionId) {
    const section = submission.sections.find((s) => s.sectionId === comment.sectionId);
    if (section) return { label: section.sectionTitle, itemId: `form:${section.sectionId}` };
  }
  if (comment.documentRequestId) {
    const doc = submission.documents.find(
      (d) => d.documentRequestId === comment.documentRequestId,
    );
    if (doc) return { label: doc.requestLabel, itemId: `document:${doc.documentId}` };
  }
  return null;
}

export interface StagingStats {
  total: number;
  approved: number;
  returned: number;
  pending: number;
  filed: number;
  unfiled: number;
  flagged: number;
  scanning: number;
  pct: number;
}

export function computeStats(items: DealStagingReviewItem[]): StagingStats {
  let approved = 0, returned = 0, filed = 0, flagged = 0, scanning = 0;
  for (const i of items) {
    if (i.reviewStatus === "Approved") approved++;
    if (i.reviewStatus === "Returned") returned++;
    if (i.isFiled) filed++;
    if (itemFlagged(i)) flagged++;
    if (itemScanning(i)) scanning++;
  }
  const total = items.length;
  const pending = total - approved - returned;
  const unfiled = total - filed;
  const pct = total ? Math.round((approved / total) * 100) : 0;
  return { total, approved, returned, pending, filed, unfiled, flagged, scanning, pct };
}

function isStagingMutable(status: DealSubmissionStatus): boolean {
  return status === "Submitted" || status === "InStaging";
}

// Returning a section/item returns the whole deal (ReturnedForChanges), so the
// flag/return controls must stay enabled on an already-returned deal — a
// liaison can keep flagging more sections in the same sitting. Approve/file
// stay gated on isStagingMutable (you can't approve a deal that's with the broker).
function isReturnable(status: DealSubmissionStatus): boolean {
  return status === "Submitted" || status === "InStaging" || status === "ReturnedForChanges";
}

// Create Deal is only valid once the submission is in staging (i.e. claimed) or
// later. The backend rejects Submitted/Draft/ReturnedForChanges/terminal with a
// 400 ("must be InStaging, Approved, or Imported"); mirror that on the button so
// a reviewer can't fire an import that's guaranteed to fail.
function isImportableStatus(status: DealSubmissionStatus): boolean {
  return status === "InStaging" || status === "Approved" || status === "Imported";
}

// The single gate for the combined "Approve & Create Deal" action. Returns a
// human-readable reason the action is blocked, or undefined when it's allowed.
// Import (the backend `POST deal-submission/{id}/import`) already accepts an
// InStaging submission and runs the final approval + pipeline entry atomically,
// so this mirrors that endpoint's status and filing guards — importable status
// AND every Forms & Documents item approved and filed. The import's field
// validation can still refuse (see parseImportBlockReasons). Exported for unit
// tests.
// `returnedBlockedReason` is the ONE origin-dependent string here (a returned
// escalation is sitting with the Buyer Consultant, not a broker); it's a
// trailing parameter defaulted to the broker wording so the gate stays a pure,
// two-argument function for the existing callers and tests.
export function computeCreateDealBlockedReason(
  status: DealSubmissionStatus,
  stats: StagingStats,
  returnedBlockedReason = "Returned to the broker — wait for resubmission before creating the deal",
): string | undefined {
  if (!isImportableStatus(status)) {
    if (status === "Submitted") return "Claim this submission for review before creating the deal";
    if (status === "ReturnedForChanges") return returnedBlockedReason;
    return "This submission isn't in a state that can be imported";
  }
  if (stats.unfiled > 0) return `${stats.unfiled} item${stats.unfiled === 1 ? "" : "s"} left to file`;
  if (stats.returned > 0) return `${stats.returned} item${stats.returned === 1 ? "" : "s"} returned — resolve before filing`;
  return undefined;
}

// The "Claim for review" / "Begin review" affordance shows for any Submitted
// submission a reviewer can act on — INCLUDING one that already carries a
// pre-assigned liaison. Submit pre-assigns AssignedReviewerUserId, so gating
// the button on "no reviewer yet" hid the only Submitted→InStaging control and
// dead-ended import (a 100%-reviewed submission stayed stuck at Submitted with
// no way to claim). Exported for unit tests.
export function canShowClaimAction(
  status: DealSubmissionStatus,
  canReview: boolean,
): boolean {
  return status === "Submitted" && canReview;
}

// Who the claim is attributed to: keep the assigned liaison if there is one
// (begin review without stealing the assignment), else claim to self. An
// explicit id is required — an empty-body claim is rejected by the backend when
// a different reviewer already holds it. Returns null when there's nobody to
// attribute it to, in which case the button stays disabled. Exported for tests.
export function claimReviewerId(
  claimedBy: string | null | undefined,
  currentUserId: string | null | undefined,
): string | null {
  return claimedBy ?? currentUserId ?? null;
}

// Pull field-tagged Create Deal block reasons out of an ApiError's `details`
// payload (the import endpoint returns them alongside the flat `errors[]`).
// Returns [] for ordinary errors so the caller falls back to a plain banner.
export function parseImportBlockReasons(err: unknown): DealImportBlockReason[] {
  const details = err instanceof ApiError ? err.details : undefined;
  if (!Array.isArray(details)) return [];
  return details.filter(
    (r): r is DealImportBlockReason =>
      typeof r === "object" && r !== null && typeof (r as { message?: unknown }).message === "string",
  );
}

interface SectionEditorProps {
  section: DealStagingSection;
  disabled: boolean;
  onSave: (savedDataJson: string) => Promise<void>;
  /** Optional so every existing caller/test keeps compiling; absent (or
   *  BrokerPortal) means no liaison-supplied note, i.e. today's behaviour. */
  origin?: DealSubmissionOrigin;
}

/** One level of flattening so a `mapsTo` lookup can't miss a repeat-group child. */
function flattenFields(fields: FormField[]): FormField[] {
  return fields.flatMap((f) => (f.fields?.length ? [f, ...f.fields] : [f]));
}

/** Nothing supplied yet. A numeric 0 counts as SUPPLIED — it's a real answer
 *  for a currency field, and nagging about it would be wrong. */
function isBlankAnswer(value: unknown): boolean {
  if (value === undefined || value === null) return true;
  return typeof value === "string" && value.trim() === "";
}

export interface PendingLiaisonField extends LiaisonSuppliedField {
  /** The schema's own label, so the note names the field the liaison sees. */
  label: string;
}

/**
 * Deal Submission spec §7 — the liaison-supplied fields this section actually carries
 * that are still blank.
 *
 * Returns [] for every broker submission and for any section that maps to none
 * of them, so the note appears only on an escalation's Business section — and
 * clears itself field by field as the liaison fills them in. Exported for unit
 * tests.
 */
export function pendingLiaisonFields(
  origin: DealSubmissionOrigin | null | undefined,
  schema: FormSchema | null,
  data: PortalFormData,
): PendingLiaisonField[] {
  // `liaisonSuppliedField` re-checks this per field; gating here as well keeps
  // the rule where a reader looks for it and skips the walk entirely for the
  // broker submissions that are almost all of the traffic.
  if (!isEscalation(origin) || !schema) return [];
  return flattenFields(schema.fields).flatMap((field) => {
    const supplied = liaisonSuppliedField(origin, field.mapsTo);
    if (!supplied || !isBlankAnswer(data[field.name])) return [];
    return [{ ...supplied, label: field.label }];
  });
}

/**
 * Deal Submission spec §7 — assigned work, NOT an error.
 *
 * An escalation arrives with these three business fields deliberately empty
 * because the buyer module's equivalents look right and mean something else. A
 * liaison who reads three blanks as a broken submission bounces it back to the
 * specialist, who can't fill them either. So this states the expectation in a
 * neutral, informational tone — no warning colour, no alert icon — and adds NO
 * validation gate (import already refuses without the legal name and NAICS
 * code; staging stays permissive so partial work can be saved mid-review).
 */
function LiaisonSuppliedFieldsNote({ fields }: { fields: PendingLiaisonField[] }) {
  if (fields.length === 0) return null;
  return (
    <div
      className="rounded-md border border-border-light bg-white-warm p-space-3 flex flex-col gap-space-2"
      data-testid="liaison-supplied-fields"
    >
      <div className="flex items-center gap-space-2">
        <PenLine size={14} className="shrink-0 text-gray-mid" aria-hidden />
        <h4 className="m-0 text-small font-semibold text-onyx">For you to supply</h4>
      </div>
      <p className="m-0 text-caption text-gray-mid">
        A buyer escalation leaves {fields.length === 1 ? "this field" : "these fields"} blank on
        purpose — the buyer submission holds a value that looks right but means something else.
      </p>
      <ul className="m-0 flex list-none flex-col gap-space-1 p-0">
        {fields.map((f) => (
          <li key={f.mapsTo} className="text-caption text-onyx">
            <span className="font-semibold">{f.label}</span>
            <span className="text-gray-mid"> — {f.why}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

// Edits use the SAME renderer as the broker portal (PortalFormRenderer), so this
// staff-side correction surface enforces identical per-type inputs, masks, and
// format validation: number fields reject non-numeric input, yes/no renders as a
// boolean toggle (not an editable string), email/phone are format-checked, etc.
// The sibling Collection staging panel (StagedSubmissionReviewPanel) reuses the
// same renderer. Structured values (repeat groups, address) round-trip as real
// objects, so nothing is stringified to "[object Object]".
function parseSavedFormData(blob: string | null | undefined): PortalFormData {
  if (!blob) return {};
  try {
    const parsed = JSON.parse(blob) as unknown;
    return parsed && typeof parsed === "object" && !Array.isArray(parsed)
      ? (parsed as PortalFormData)
      : {};
  } catch {
    return {};
  }
}

export function SectionEditor({ section, disabled, onSave, origin }: SectionEditorProps) {
  const [schema, setSchema] = useState<FormSchema | null>(null);
  const [data, setData] = useState<PortalFormData>({});
  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  useEffect(() => {
    const parsed = section.formSchemaJson ? parseFormSchema(section.formSchemaJson) : null;
    setSchema(parsed ? normalizeDealSubmissionFinancialSchema(parsed) : parsed);
    setData(parseSavedFormData(section.savedDataJson));
    setErrorMsg(null);
  }, [section.sectionId, section.formSchemaJson, section.savedDataJson]);

  async function handleSave() {
    if (!schema) return;
    // Mirror the broker portal's DataEntrySection save guard: never persist a
    // malformed email / phone. (Format only — required/completeness is the
    // broker's submit-time gate, not a staff correction concern.)
    const formatError = findFirstFormatError(schema, data);
    if (formatError) {
      setErrorMsg(`"${formatError.field.label}" — ${formatError.message}`);
      return;
    }
    setErrorMsg(null);
    setSaving(true);
    try {
      await onSave(JSON.stringify(data));
    } finally {
      setSaving(false);
    }
  }

  // Live off the editor's own `data` state (seeded from savedDataJson), so the
  // note shrinks as the liaison types rather than waiting for a save round-trip.
  const pendingSupplied = pendingLiaisonFields(origin, schema, data);

  if (!schema) {
    return (
      <Card className="p-space-4 text-small text-gray-mid">
        This section's clean generated answer document is available in the preview. Field editing is unavailable because the form schema could not be parsed.
      </Card>
    );
  }

  return (
    <Card className="p-space-4 flex flex-col gap-space-3">
      <LiaisonSuppliedFieldsNote fields={pendingSupplied} />
      <PortalFormRenderer schema={schema} value={data} onChange={setData} disabled={disabled} />
      {errorMsg && <p className="text-caption text-status-error m-0" role="alert">{errorMsg}</p>}
      <div className="flex justify-end">
        <Button variant="secondary" size="sm" loading={saving} disabled={disabled} onClick={handleSave}>Save edits</Button>
      </div>
    </Card>
  );
}

type ListFilter = "" | "flagged" | "scanning" | "returned";

export function DealStagingReviewPage() {
  const { id = "" } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { hasPermission, user } = useAuth();
  const fmt = useDateFormatter();
  const { toast } = useToast();
  const canOverrideScan = hasPermission("DealSubmission.OverrideScan");
  // Regenerate/Refresh are benign recovery actions — available to any reviewer,
  // not just the elevated scan-override role.
  const canReview = hasPermission("DealSubmission.Review");
  const canImport = hasPermission("DealSubmission.Import");
  const canReassign = hasPermission("Deals.Reassign") || hasPermission("DealSubmission.Review");

  const detail = useQuery({
    queryKey: stagingKeys.detail(id),
    queryFn: () => dealStagingApi.get(id),
    enabled: Boolean(id),
    // Poll while any section assessment is still generating so the panel resolves
    // to Completed/Failed on its own (and the server watchdog is tripped). The
    // detail query otherwise inherits the app's 5-min staleTime and never
    // re-reads, so a finished/stuck run keeps showing "Generating…" indefinitely.
    refetchInterval: (query) => {
      // Session expired — stop polling so an unattended tab doesn't 401 forever.
      if (isSessionExpired(query.state.error)) return false;
      const generating = query.state.data?.sections?.some(
        (s) => s.assessmentStatus === "Pending" && s.assessmentStartedAt,
      );
      return generating ? 5000 : false;
    },
    retry: retryUnlessSessionExpired,
  });
  const submission = detail.data;

  useBreadcrumbs([
    { label: "Deals", href: "/deal-flow/pipeline" },
    { label: "Staging and Review", href: "/deal-flow/deal-staging" },
    { label: submission?.submission.title ?? "Review" },
  ]);

  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
  const selectedItem = submission?.reviewItems.find((i) => i.itemId === selectedItemId) ?? null;
  const [returnBody, setReturnBody] = useState("");
  const [returnOpen, setReturnOpen] = useState(false);
  const [declineBrokerReason, setDeclineBrokerReason] = useState("");
  const [declineInternalReason, setDeclineInternalReason] = useState("");
  const [declineOpen, setDeclineOpen] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  // Field-tagged Create Deal block reasons (jump-to-field). Empty for ordinary
  // errors, which fall back to the plain `actionError` banner.
  const [importBlockReasons, setImportBlockReasons] = useState<DealImportBlockReason[]>([]);
  const [selectedLiaison, setSelectedLiaison] = useState("");
  const [approveRemainingOpen, setApproveRemainingOpen] = useState(false);

  // List filtering + bulk selection (client-side over already-loaded data).
  const [filter, setFilter] = useState<ListFilter>("");
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkApproveOpen, setBulkApproveOpen] = useState(false);
  const [bulkFlagOpen, setBulkFlagOpen] = useState(false);

  const authorIds = useMemo(
    () => (submission?.returnComments.map((c) => c.authorUserId).filter(Boolean) as string[] | undefined) ?? [],
    [submission],
  );
  const authorLabels = useInternalUserLabels(authorIds);

  const c1Users = useAssignableUsers("C1");

  useEffect(() => {
    setSelectedLiaison(submission?.submission.assignedReviewerUserId ?? "");
  }, [submission?.submission.assignedReviewerUserId]);

  function invalidate() {
    void queryClient.invalidateQueries({ queryKey: stagingKeys.detail(id) });
    void queryClient.invalidateQueries({ queryKey: stagingKeys.all });
  }
  const onError = (err: unknown) => setActionError(extractErrorMessage(err));

  const claim = useMutation({
    // Pass an explicit reviewer so claiming a submission that was pre-assigned
    // a liaison at submit doesn't trip the backend's "already claimed by
    // another reviewer" guard (which only fires for an empty-body self-claim).
    mutationFn: (assignedReviewerUserId: string) => dealStagingApi.claim(id, { assignedReviewerUserId }),
    onSuccess: invalidate,
    onError,
  });
  const reassign = useMutation({
    mutationFn: (assignedReviewerUserId: string | null) => dealStagingApi.reassign(id, { assignedReviewerUserId }),
    onSuccess: invalidate,
    onError,
  });
  const returnMut = useMutation({
    mutationFn: (body: string) => dealStagingApi.return(id, { body }),
    onSuccess: () => { invalidate(); navigate("/deal-flow/deal-staging"); },
    onError,
  });
  const importMut = useMutation({
    mutationFn: () => dealStagingApi.import(id),
    onMutate: () => { setActionError(null); setImportBlockReasons([]); },
    onSuccess: (result) => {
      // Import puts the deal in the pipeline — also refresh the deal lists so the
      // new deal appears in the pipeline without a manual refresh.
      invalidateAfterImport(queryClient, id);
      // Non-fatal data-quality warnings (e.g. an unsalvageable broker phone was
      // cleared). The import still succeeded; surface them before we navigate.
      if (result.importWarnings?.length) {
        toast({ tone: "info", message: result.importWarnings.join(" ") });
      }
      navigate(`/deal-flow/deals/${result.dealId}`);
    },
    onError: (err: unknown) => {
      // Prefer field-tagged reasons (jump-to-field); fall back to a plain banner.
      const reasons = parseImportBlockReasons(err);
      setImportBlockReasons(reasons);
      if (reasons.length === 0) setActionError(extractErrorMessage(err));
    },
  });
  const declineMut = useMutation({
    mutationFn: () => dealStagingApi.decline(id, {
      brokerVisibleReason: declineBrokerReason.trim(),
      internalReason: declineInternalReason.trim() || null,
    }),
    onSuccess: () => { invalidate(); navigate("/deal-flow/deal-staging"); },
    onError,
  });
  const approveRemaining = useMutation({
    mutationFn: () => dealStagingApi.approveRemainingSections(id),
    onSuccess: () => { invalidate(); setApproveRemainingOpen(false); },
    onError,
  });

  async function saveSectionEdit(sectionId: string, savedDataJson: string) {
    setActionError(null);
    // Clear stale Create Deal block reasons; the reviewer just corrected a field.
    setImportBlockReasons([]);
    try { await dealStagingApi.editSection(id, { sectionId, savedDataJson }); invalidate(); }
    catch (err) { onError(err); }
  }
  async function overrideScan(documentId: string, reason: string) {
    setActionError(null);
    try { await dealStagingApi.overrideScan(id, { documentId, reason }); invalidate(); }
    catch (err) { onError(err); }
  }

  const stats = useMemo(() => computeStats(submission?.reviewItems ?? []), [submission]);

  if (!submission) return <div className="p-space-4 text-small text-gray-mid">Loading staging review…</div>;

  // Resolved ONCE for the page and threaded into the sub-components that render
  // counterparty-facing copy, so a broker-voiced sentence can't survive on an
  // escalation because one call site was missed.
  const origin = submission.submission.origin;
  const copy = stagingCopyFor(origin);

  const mutable = isStagingMutable(submission.submission.status);
  // Corrections (section edits only) are also allowed while Approved, so a
  // reviewer can fix a value that fails Create Deal without un-approving.
  const correctable = mutable || submission.submission.status === "Approved";
  const returnable = isReturnable(submission.submission.status);
  const declinable = submission.submission.status === "Submitted" || submission.submission.status === "InStaging" || submission.submission.status === "ReturnedForChanges";
  const claimedBy = submission.submission.assignedReviewerUserId;
  const claimReviewer = claimReviewerId(claimedBy, user?.id);
  const createDealBlockedReason = computeCreateDealBlockedReason(submission.submission.status, stats, copy.returnedBlockedReason);
  const filedPct = stats.total ? Math.round((stats.filed / stats.total) * 100) : 0;
  // The filed progress bar now owns the "items left to file" story, so only
  // surface the OTHER Create Deal block reasons (claim / status / returned) as
  // visible text — never the redundant unfiled count.
  const filingBlocks = isImportableStatus(submission.submission.status) && stats.unfiled > 0;

  // D1 — reverse visibility: did the broker see/act on the return? Surface
  // the return → addressed → resubmit timeline from data we already track
  // (broker-visible events + per-comment resolution).
  const latestEventAt = (type: DealSubmissionEventType): string | null =>
    submission.events
      .filter((e) => e.type === type)
      .map((e) => e.createdAt)
      .sort()
      .at(-1) ?? null;
  const returnedAt = latestEventAt("Returned");
  const resubmittedAt = latestEventAt("Resubmitted");
  const addressedCount = submission.returnComments.filter((c) => c.resolved).length;

  const toggleFilter = (next: ListFilter) => setFilter((cur) => (cur === next ? "" : next));

  const rowMatches = (i: DealStagingReviewItem): boolean => {
    if (!filter) return true;
    if (filter === "flagged") return itemFlagged(i);
    if (filter === "scanning") return itemScanning(i);
    return i.reviewStatus === "Returned";
  };

  // Group items by section (form parent + nested documents), then split into
  // "needs attention" vs "ready to review" while preserving submission order.
  const groups = submission.sections.map((section) => {
    const form = submission.reviewItems.find((i) => i.itemId === `form:${section.sectionId}`) ?? null;
    const docs = submission.reviewItems.filter((i) => i.kind === "Document" && i.sectionId === section.sectionId);
    const attention = [form, ...docs].some((i) => i != null && (itemFlagged(i) || itemScanning(i)));
    return { section, form, docs, attention };
  });
  const groupHasVisible = (g: (typeof groups)[number]) =>
    (g.form != null && rowMatches(g.form)) || g.docs.some(rowMatches);
  const needsAttention = groups.filter((g) => g.attention && groupHasVisible(g));
  const ready = groups.filter((g) => !g.attention && groupHasVisible(g));
  const nothingVisible = needsAttention.length === 0 && ready.length === 0;

  const toggleSelect = (itemId: string, checked: boolean) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(itemId); else next.delete(itemId);
      return next;
    });
  const clearSelection = () => setSelectedIds(new Set());
  const selectedItems = submission.reviewItems.filter((i) => selectedIds.has(i.itemId));
  const approvableSelected = selectedItems.filter((i) => !i.isFiled && (i.kind === "Form" || i.scanStatus === "Clean"));

  const renderGroup = (g: (typeof groups)[number], isAttention: boolean) => {
    const visibleDocs = g.docs.filter(rowMatches);
    const showForm = g.form != null && (rowMatches(g.form) || visibleDocs.length > 0);
    return (
      <div
        key={g.section.sectionId}
        className={cn(
          "rounded border border-border-light p-space-2 flex flex-col gap-space-1",
          isAttention && "border-l-4 border-l-status-warning",
        )}
      >
        {showForm && g.form && (
          <ItemRow item={g.form} selected={selectedIds.has(g.form.itemId)} onToggleSelect={toggleSelect} onOpen={() => setSelectedItemId(g.form!.itemId)} />
        )}
        {visibleDocs.length > 0 && (
          <div className="ml-space-4 border-l border-border-light pl-space-2 flex flex-col gap-space-1">
            {visibleDocs.map((doc) => (
              <ItemRow key={doc.itemId} item={doc} nested selected={selectedIds.has(doc.itemId)} onToggleSelect={toggleSelect} onOpen={() => setSelectedItemId(doc.itemId)} />
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="flex flex-col gap-space-4">
      <PageHeader
        title={submission.submission.title ?? "(untitled submission)"}
        description={<>{submission.templateName} v{submission.submission.templateVersion}</>}
        actions={<div className="flex items-center gap-space-3">{submission.submission.importedDealId && <Link to={`/deal-flow/deals/${submission.submission.importedDealId}`} className="text-small text-gold-shadow hover:underline">{submission.pipelineDealNumber ? `View deal ${submission.pipelineDealNumber}` : "View linked deal"}</Link>}<Link to="/deal-flow/deal-staging" className="text-small text-onyx hover:text-gold-shadow"><ArrowLeft size={14} className="mr-1 inline" />Back to queue</Link></div>}
      />

      {/* Deal context header */}
      <Card className="p-space-4 flex flex-col gap-space-3">
        <div className="flex flex-wrap items-center gap-space-3">
          <StatusBadge status={submission.submission.status} />
          {canShowClaimAction(submission.submission.status, canReview) && <Button variant="secondary" size="sm" loading={claim.isPending} disabled={!claimReviewer} onClick={() => claimReviewer && claim.mutate(claimReviewer)}>{claimedBy ? "Begin review" : "Claim for review"}</Button>}
        </div>
        <dl className="grid grid-cols-2 md:grid-cols-3 gap-space-3 m-0 text-small">
          <Field label="Deal ID" value={submission.pipelineDealNumber ?? "Not yet assigned"} />
          <Field label="Template">{submission.templateName} v{submission.submission.templateVersion}</Field>
          <Field label="Origin" value={copy.originDescription} />
          {/* The Broker field stays on BOTH origins and is not a mislabel: on an
              escalation the listing agent IS the broker of record on the deal,
              their Broker row exists, and both links resolve. */}
          <Field label="Broker"><BrokerRecordLink brokerId={submission.submission.brokerId} label={submission.brokerName ?? "View broker record"} /> · <PortalAccountLink portalUserId={submission.brokerPortalUserId} /></Field>
          <Field label="Broker Liaison" value={submission.assignedReviewerDisplayName ?? "Unassigned"} />
          <Field label="Submitted" value={submission.submission.submittedAt ? fmt.dateTime(submission.submission.submittedAt) : "—"} />
          <OriginatingBuyerSubmissionField submission={submission} />
        </dl>
      </Card>

      {actionError && <Card className="border-red-600 text-small text-red-700">{actionError}</Card>}

      {importBlockReasons.length > 0 && (
        <Card className="border-red-600 flex flex-col gap-space-2">
          <div className="flex items-center gap-space-2 text-small font-semibold text-red-700">
            <AlertTriangle size={16} /> Create Deal is blocked — fix the field{importBlockReasons.length === 1 ? "" : "s"} below, then try again.
          </div>
          <ul className="flex flex-col gap-space-2 m-0 list-none p-0">
            {importBlockReasons.map((r, idx) => {
              const sectionTitle = r.sectionId
                ? submission.sections.find((s) => s.sectionId === r.sectionId)?.sectionTitle ?? null
                : null;
              const canJump = Boolean(r.sectionId && submission.reviewItems.some((i) => i.itemId === `form:${r.sectionId}`));
              return (
                <li key={idx} className="flex flex-wrap items-center justify-between gap-space-2 text-small text-red-700">
                  <span>{r.message}{sectionTitle ? ` (${sectionTitle})` : ""}</span>
                  {canJump && (
                    <Button variant="secondary" size="sm" onClick={() => setSelectedItemId(`form:${r.sectionId}`)}>
                      Fix this field
                    </Button>
                  )}
                </li>
              );
            })}
          </ul>
        </Card>
      )}

      {/* Progress indicators (reviewed + filed) + filter chips */}
      <Card className="p-space-4 flex flex-col gap-space-3">
        <div className="flex flex-col gap-space-1">
          <div className="flex items-center justify-between gap-space-2">
            <span className="text-small font-semibold text-onyx">{stats.approved} of {stats.total} reviewed</span>
            <span className="text-small text-gray-mid tabular-nums">{stats.pct}%</span>
          </div>
          <ProgressBar value={stats.pct} label="Items reviewed" />
        </div>
        <div className="flex flex-col gap-space-1">
          <div className="flex items-center justify-between gap-space-2">
            <span className="text-small font-semibold text-onyx">{stats.filed} of {stats.total} filed</span>
            <span className="text-small text-gray-mid tabular-nums">{filedPct}%</span>
          </div>
          <ProgressBar value={filedPct} label="Items filed" />
        </div>
        {(stats.flagged > 0 || stats.scanning > 0 || stats.returned > 0) && (
          <div className="flex flex-wrap items-center gap-space-2">
            {stats.flagged > 0 && <CountChip loud label="Flagged" count={stats.flagged} active={filter === "flagged"} onClick={() => toggleFilter("flagged")} />}
            {stats.scanning > 0 && <CountChip label="Scanning" count={stats.scanning} active={filter === "scanning"} onClick={() => toggleFilter("scanning")} />}
            {stats.returned > 0 && <CountChip label="Returned" count={stats.returned} active={filter === "returned"} onClick={() => toggleFilter("returned")} />}
            {filter && <button type="button" onClick={() => setFilter("")} className="text-caption text-gold-shadow hover:underline">Clear filter</button>}
          </div>
        )}
      </Card>

      {/* Final actions — full-width card with a horizontal action row */}
      <Card className="p-space-4 flex flex-col gap-space-3">
        <h3 className="text-h4 m-0">Final actions</h3>
        <div className="flex flex-wrap items-center gap-space-2">
          <Button variant="secondary" disabled={!mutable} onClick={() => setReturnOpen(true)} data-testid="return-to-broker">
            <RotateCcw size={14} className="mr-1 inline" aria-hidden />
            {copy.returnAction}{stats.returned > 0 ? ` (${stats.returned})` : ""}
          </Button>
          {declinable && canImport && (
            <Button variant="secondary" disabled={declineOpen} onClick={() => setDeclineOpen(true)} data-testid="decline-deal">Decline deal</Button>
          )}
          {/* Spacer pushes the primary action to the right; Return/Decline stay left. */}
          <div className="grow" aria-hidden />
          {canImport && (
            <span title={createDealBlockedReason}><Button disabled={Boolean(createDealBlockedReason) || importMut.isPending} loading={importMut.isPending} onClick={() => importMut.mutate()} data-testid="approve-and-create-deal">Approve & Create Deal</Button></span>
          )}
        </div>
        {canImport && createDealBlockedReason && !filingBlocks && <p className="text-caption text-status-warning m-0">{createDealBlockedReason}</p>}
        {declinable && canImport && declineOpen && (
          <div className="border-t border-border-light pt-space-3 flex flex-col gap-space-2">
            <label className="text-caption text-gray-mid">Decline broker-visible reason</label>
            <Textarea rows={2} value={declineBrokerReason} onChange={(e) => setDeclineBrokerReason(e.target.value)} />
            <label className="text-caption text-gray-mid">Internal reason (optional)</label>
            <Textarea rows={2} value={declineInternalReason} onChange={(e) => setDeclineInternalReason(e.target.value)} />
            <div className="flex items-center justify-end gap-space-2">
              <button type="button" onClick={() => setDeclineOpen(false)} className="text-caption text-gray-mid hover:underline">Cancel</button>
              <Button variant="destructive" size="sm" disabled={!declineBrokerReason.trim()} loading={declineMut.isPending} onClick={() => declineMut.mutate()}>Decline</Button>
            </div>
          </div>
        )}
      </Card>

      <Card className="p-space-4 flex flex-col gap-space-4">
        <div>
          <h2 className="text-h3 m-0">Forms & Documents</h2>
          <p className="text-small text-gray-mid m-0">Items needing attention are pulled to the top. Uploaded documents are nested under their parent form and reviewed/filed individually.</p>
        </div>

        {nothingVisible ? (
          <p className="text-caption text-gray-mid m-0">No items match this filter.</p>
        ) : (
          <>
            {needsAttention.length > 0 && (
              <div className="flex flex-col gap-space-2">
                <h3 className="text-caption font-semibold uppercase tracking-wide text-gray-mid m-0">Needs attention</h3>
                {needsAttention.map((g) => renderGroup(g, true))}
              </div>
            )}
            {ready.length > 0 && (
              <div className="flex flex-col gap-space-2">
                <h3 className="text-caption font-semibold uppercase tracking-wide text-gray-mid m-0">Ready to review</h3>
                {ready.map((g) => renderGroup(g, false))}
              </div>
            )}
          </>
        )}
      </Card>

      {submission.returnComments.length > 0 && (
        <Card className="p-space-4">
          <h3 className="text-h4">Return history</h3>
          {/* D1 — close the loop the reviewer's way: when was it returned,
              how much has the broker addressed, has it come back. */}
          <p className="text-caption text-gray-mid m-0 mb-space-2" data-testid="return-loop-status">
            {returnedAt ? `Returned ${fmt.relative(returnedAt)}` : "Returned"}
            {` · ${addressedCount} of ${submission.returnComments.length} addressed by ${copy.addressedByLabel}`}
            {resubmittedAt && ` · Resubmitted ${fmt.relative(resubmittedAt)}`}
          </p>
          <ul className="flex flex-col gap-space-2">
            {submission.returnComments.map((c) => {
              const ref = commentItemRef(submission, c);
              return (
                <li key={c.id} className="text-small">
                  <span className="text-gray-mid">
                    {c.authorUserId ? authorLabels.lookup(c.authorUserId)?.displayName ?? fallbackUserLabel(c.authorUserId) : "Reviewer"} · {fmt.dateTime(c.createdAt)}
                  </span>
                  {ref && (
                    <button
                      type="button"
                      onClick={() => setSelectedItemId(ref.itemId)}
                      className="ml-space-2 inline-flex items-center gap-space-1 rounded-pill border border-border-light bg-white-warm px-space-2 py-[1px] text-caption font-medium text-onyx hover:border-gold hover:text-gold-shadow focus-visible:outline-none focus-visible:shadow-goldRing"
                      data-testid={`return-comment-item-${c.id}`}
                    >
                      <FileText size={12} aria-hidden /> {ref.label}
                    </button>
                  )}
                  {c.resolved && (
                    <span
                      className="ml-space-2 text-caption font-medium text-status-success"
                      data-testid={`return-comment-addressed-${c.id}`}
                    >
                      {`✓ ${copy.addressedBadgeLabel}`}{c.resolvedAt ? ` ${fmt.relative(c.resolvedAt)}` : ""}
                    </span>
                  )}
                  <p className="m-0 whitespace-pre-wrap">{c.body}</p>
                </li>
              );
            })}
          </ul>
        </Card>
      )}

      {(submission.responses?.length ?? 0) > 0 && (
        <CounterpartyResponsesPanel
          submission={submission}
          dealId={submission.submission.importedDealId}
          copy={copy}
          onSaved={invalidate}
        />
      )}

      {canReassign && (
        <Card>
          <div className="flex flex-col md:flex-row md:items-end gap-space-3">
            <label className="text-small text-onyx md:min-w-[20rem]">Reassign Broker Liaison
              <select className="mt-space-1 w-full border border-border-light rounded-sm px-space-2 py-space-1 text-small" value={selectedLiaison} onChange={(e) => setSelectedLiaison(e.target.value)}>
                <option value="">Unassigned</option>
                {(c1Users.data ?? []).map((u) => <option key={u.id} value={u.id}>{u.displayName ?? u.email}</option>)}
              </select>
            </label>
            <Button variant="secondary" size="sm" loading={reassign.isPending} onClick={() => reassign.mutate(selectedLiaison || null)}>Save assignment</Button>
          </div>
        </Card>
      )}

      {/* Sticky bulk action bar */}
      {selectedItems.length > 0 && (
        <div className="sticky bottom-space-4 z-20 flex flex-wrap items-center justify-between gap-space-3 rounded-lg border border-gold bg-white-pure px-space-4 py-space-3 shadow-md">
          <span className="text-small font-semibold text-onyx">{selectedItems.length} selected</span>
          <div className="flex items-center gap-space-2">
            <Button variant="secondary" size="sm" onClick={clearSelection}>Clear</Button>
            <Button variant="secondary" size="sm" disabled={!returnable} onClick={() => setBulkFlagOpen(true)}>Flag</Button>
            <Button size="sm" disabled={!mutable || approvableSelected.length === 0} onClick={() => setBulkApproveOpen(true)}>Approve{approvableSelected.length > 0 ? ` (${approvableSelected.length})` : ""}</Button>
          </div>
        </div>
      )}

      {selectedItem && <ReviewItemDrawer submission={submission} item={selectedItem} origin={origin} copy={copy} mutable={mutable} correctable={correctable} returnable={returnable} canOverrideScan={canOverrideScan} canReview={canReview} onClose={() => setSelectedItemId(null)} onSelectItem={setSelectedItemId} onSaved={invalidate} onSaveSectionEdit={saveSectionEdit} onOverrideScan={overrideScan} />}

      {bulkFlagOpen && <BulkFlagModal submissionId={id} items={selectedItems} copy={copy} refresh={invalidate} onClose={() => setBulkFlagOpen(false)} onDone={() => { invalidate(); clearSelection(); setBulkFlagOpen(false); }} />}
      {bulkApproveOpen && <BulkApproveModal submissionId={id} dealId={submission.submission.importedDealId} items={approvableSelected} refresh={invalidate} onClose={() => setBulkApproveOpen(false)} onDone={() => { invalidate(); clearSelection(); setBulkApproveOpen(false); }} />}

      <ConfirmModal open={approveRemainingOpen} title="Approve remaining items?" message="Pending items will be marked approved, but Create Deal still requires each item to be filed." confirmLabel="Approve remaining" onConfirm={() => approveRemaining.mutate()} onCancel={() => setApproveRemainingOpen(false)} />
      <Modal
        open={returnOpen}
        onClose={() => { if (returnMut.isPending) return; setReturnOpen(false); }}
        size="sm"
        title={copy.returnAction}
        description={copy.returnModalDescription}
        footer={
          <>
            <Button variant="secondary" size="sm" onClick={() => setReturnOpen(false)} disabled={returnMut.isPending}>Cancel</Button>
            <Button variant="destructive" size="sm" disabled={!returnBody.trim()} loading={returnMut.isPending} onClick={() => returnMut.mutate(returnBody.trim())} data-testid="return-to-broker-confirm">{copy.returnAction}</Button>
          </>
        }
      >
        <Textarea rows={3} value={returnBody} onChange={(e) => setReturnBody(e.target.value)} placeholder={copy.returnNotePlaceholder} aria-label={copy.returnNoteAriaLabel} />
      </Modal>
    </div>
  );
}

// Append-only counterparty responses to returned items — the broker on a portal
// submission, the Buyer Consultant on an escalation. Each response is a NEW,
// viewable artifact (their text + optional document). The document opens in
// the same viewer used elsewhere in staging but WITHOUT the AI assessment
// panel, and can be filed as a deal-level document. A response never changes
// the submission's status.
function CounterpartyResponsesPanel({ submission, dealId, copy, onSaved }: {
  submission: DealStagingDetail;
  dealId: string | null;
  copy: StagingOriginCopy;
  onSaved: () => void;
}) {
  const responses = submission.responses ?? [];
  const commentBody = (commentId: string) =>
    submission.returnComments.find((c) => c.id === commentId)?.body ?? null;
  return (
    <Card className="p-space-4" data-testid="broker-responses-panel">
      <h3 className="text-h4">{copy.responsesPanelTitle}</h3>
      <p className="text-caption text-gray-mid m-0 mb-space-2">
        {copy.responsesPanelDescription}
      </p>
      <ul className="flex flex-col gap-space-3">
        {responses.map((r) => (
          <ResponseCard
            key={r.id}
            submissionId={submission.submission.id}
            response={r}
            commentContext={commentBody(r.returnCommentId)}
            dealId={dealId}
            copy={copy}
            onSaved={onSaved}
          />
        ))}
      </ul>
    </Card>
  );
}

function ResponseCard({ submissionId, response, commentContext, dealId, copy, onSaved }: {
  submissionId: string;
  response: DealSubmissionResponse;
  commentContext: string | null;
  dealId: string | null;
  copy: StagingOriginCopy;
  onSaved: () => void;
}) {
  const { toast } = useToast();
  const fmt = useDateFormatter();
  const [viewing, setViewing] = useState(false);
  const [filing, setFiling] = useState(false);
  const [folderId, setFolderId] = useState<string | null>(response.filedFolderId);

  const canView = response.hasDocument && response.scanStatus === "Clean";
  const alreadyFiled = Boolean(response.filedDocumentId);

  const downloadUrl = useQuery({
    queryKey: ["deal-staging-response-doc", submissionId, response.id],
    queryFn: () => dealStagingApi.responseDownloadUrl(submissionId, response.id),
    enabled: viewing && canView,
  });

  const fileMut = useMutation({
    mutationFn: () => dealStagingApi.fileResponse(submissionId, response.id, { folderId: folderId! }),
    onSuccess: () => {
      toast({ tone: "success", message: copy.responseFiledToast });
      setFiling(false);
      onSaved();
    },
    onError: (e) => toast({ tone: "error", message: extractErrorMessage(e) }),
  });

  // `url` downloads the original; `previewUrl` (when present) is a converted PDF
  // for inline preview. Render the inline URL, but keep Download on the original.
  const originalUrl = downloadUrl.data?.url ?? null;
  const inlineUrl = downloadUrl.data?.previewUrl ?? originalUrl;
  const preview: PreviewDocument = {
    title: response.originalFileName ?? copy.responseDocumentTitle,
    fileName: response.originalFileName,
    contentType: response.contentType,
    url: inlineUrl,
    isPdf: downloadUrl.data?.isPdf,
    state: downloadUrl.isLoading ? "loading" : inlineUrl ? "ready" : "unavailable",
    onDownload: originalUrl ? () => window.open(originalUrl, "_blank", "noopener,noreferrer") : undefined,
  };

  return (
    <li className="rounded-md border border-border-light bg-white-pure p-space-3" data-testid={`broker-response-${response.id}`}>
      <p className="m-0 text-caption text-gray-mid">
        <span className="font-semibold text-onyx">{response.createdByDisplayName ?? copy.responseAuthorFallback}</span>
        {" · "}{fmt.dateTime(response.createdAt)}
      </p>
      {commentContext && (
        <p className="m-0 mt-space-1 text-caption text-gray-mid">
          Re: “{commentContext.length > 120 ? `${commentContext.slice(0, 120)}…` : commentContext}”
        </p>
      )}
      {response.body && <p className="m-0 mt-space-1 whitespace-pre-wrap text-small text-onyx">{response.body}</p>}

      {response.hasDocument && (
        <div className="mt-space-2 flex flex-wrap items-center gap-space-2">
          <span className="inline-flex items-center gap-space-1 text-small text-onyx"><FileText size={14} aria-hidden /> {response.originalFileName}</span>
          <ScanBadge status={response.scanStatus} />
          {alreadyFiled && <Badge status="finalized">Filed</Badge>}
          {canView && (
            <Button size="sm" variant="secondary" onClick={() => setViewing((v) => !v)} data-testid={`view-response-doc-${response.id}`}>
              {viewing ? "Hide document" : "View document"}
            </Button>
          )}
          {canView && !alreadyFiled && (
            <Button size="sm" variant="secondary" onClick={() => setFiling((f) => !f)} data-testid={`file-response-${response.id}`}>
              File document
            </Button>
          )}
        </div>
      )}
      {response.hasDocument && !canView && !alreadyFiled && (
        <p className="m-0 mt-space-1 text-caption text-gray-mid">
          {response.scanStatus === "Pending" ? "The document is still being scanned." : "The document can’t be opened (scan not clean)."}
        </p>
      )}

      {viewing && canView && (
        <div className="mt-space-3" data-testid={`response-viewer-${response.id}`}>
          <DocumentPreviewPane document={preview} />
        </div>
      )}

      {filing && canView && !alreadyFiled && (
        <div className="mt-space-3 rounded-md border border-gray-light p-space-3 flex flex-col gap-space-3">
          <h4 className="m-0 text-small font-semibold text-onyx">File into folder</h4>
          <FolderPicker dealId={dealId} value={folderId} onChange={setFolderId} />
          <div className="flex justify-end gap-space-2">
            <Button size="sm" variant="secondary" onClick={() => setFiling(false)} disabled={fileMut.isPending}>Cancel</Button>
            <Button size="sm" disabled={!folderId || fileMut.isPending} loading={fileMut.isPending} onClick={() => fileMut.mutate()} data-testid={`file-response-confirm-${response.id}`}>File document</Button>
          </div>
        </div>
      )}
    </li>
  );
}

/**
 * Deal Submission spec §7 — the way back to the buyer submission this deal came from.
 *
 * Renders nothing unless the server resolved an id, which covers both a broker
 * submission (no buyer row exists) and an escalation whose buyer row has since
 * been deleted. Never a dead link.
 *
 * Deliberately NOT permission-gated. The route behind it is guarded on
 * `Submissions.Read`, which `RolePermissionMap.AugmentWithBuyerCrm` grants
 * UNIVERSALLY to every non-ADMIN internal role (and ADMIN holds it via
 * `Permissions.All`), so anyone who can open Deal Staging can open the buyer
 * submission. A client-side gate here would add a failure mode without removing
 * one — but if that grant ever narrows, this link needs one.
 *
 * The labels degrade independently of the link: a soft-deleted buyer lead or
 * listing costs the name, not the destination.
 */
function OriginatingBuyerSubmissionField({ submission }: { submission: DealStagingDetail }) {
  const id = submission.originatingBuyerSubmissionId;
  if (!id) return null;
  const buyer = submission.originatingBuyerName;
  const listing = submission.originatingListingName;
  const label = buyer && listing ? `${buyer} — ${listing}` : buyer ?? listing ?? "View buyer submission";
  return (
    <Field label="Escalated from">
      <Link
        to={`/buyer-submissions/${id}`}
        className="text-gold-shadow hover:underline"
        data-testid="originating-buyer-submission"
      >
        {label}
      </Link>
    </Field>
  );
}

function Field({ label, value, children }: { label: string; value?: ReactNode; children?: ReactNode }) {
  return (
    <div className="flex flex-col gap-space-1">
      <dt className="text-caption text-gray-mid">{label}</dt>
      <dd className="m-0 text-small text-onyx">{children ?? value}</dd>
    </div>
  );
}

function ProgressBar({ value, label }: { value: number; label?: string }) {
  const clamped = Math.max(0, Math.min(100, value));
  return (
    <div className="h-2 w-full overflow-hidden rounded-pill border border-border-light bg-white-warm" role="progressbar" aria-label={label} aria-valuenow={clamped} aria-valuemin={0} aria-valuemax={100}>
      <div className="h-full rounded-pill bg-gold transition-[width] duration-fast" style={{ width: `${clamped}%` }} />
    </div>
  );
}

function CountChip({ label, count, active, loud, onClick }: { label: string; count: number; active: boolean; loud?: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={cn(
        "inline-flex items-center gap-space-1 rounded-pill border px-space-3 py-[5px] text-caption font-medium transition-colors duration-fast ease-standard focus-visible:outline-none focus-visible:shadow-goldRing",
        loud
          ? active
            ? "border-status-error bg-status-error text-white-pure"
            : "border-status-error text-status-error hover:bg-status-error/10"
          : active
            ? "border-gold bg-gold text-onyx"
            : "border-border-light bg-white-pure text-gray-mid hover:border-gold hover:text-onyx",
      )}
    >
      {loud && <AlertTriangle size={12} aria-hidden />}
      {label}
      <span className={cn("rounded-pill px-space-1 text-[10px] font-bold tabular-nums", loud ? (active ? "bg-white-pure/20" : "bg-status-error/10") : active ? "bg-onyx/10 text-onyx" : "bg-white-warm text-gray-mid")}>{count}</span>
    </button>
  );
}

function RowBadges({ item }: { item: DealStagingReviewItem }) {
  const badges: ReactNode[] = [];
  if (item.reviewStatus === "Approved") badges.push(<Badge key="approved" status="finalized">Approved</Badge>);
  if (item.isBypassed) badges.push(<Badge key="skipped" status="archived">Skipped</Badge>);
  else if (item.isFiled) badges.push(<Badge key="filed" status="finalized">Filed</Badge>);
  if (item.reviewStatus === "Returned") badges.push(<Badge key="returned" status="archived">Returned</Badge>);
  if (item.scanStatus === "Infected") badges.push(<Badge key="inf" status="error"><AlertTriangle size={11} className="mr-1 inline" aria-hidden />Infected</Badge>);
  else if (item.scanStatus === "Error") badges.push(<Badge key="scanerr" status="error"><AlertTriangle size={11} className="mr-1 inline" aria-hidden />Scan error</Badge>);
  else if (item.scanStatus === "Pending") badges.push(<Badge key="scan" status="review">Scanning…</Badge>);
  if (item.assessmentStatus === "Failed") badges.push(<Badge key="aifail" status="error"><AlertTriangle size={11} className="mr-1 inline" aria-hidden />AI failed</Badge>);
  return <>{badges}</>;
}

function ItemRow({ item, nested, selected, onToggleSelect, onOpen }: { item: DealStagingReviewItem; nested?: boolean; selected: boolean; onToggleSelect: (itemId: string, checked: boolean) => void; onOpen: () => void }) {
  const flagged = itemFlagged(item);
  return (
    <div className={cn("group flex items-center gap-space-2 rounded p-space-2 hover:bg-white-warm", flagged && "border-l-2 border-l-status-error bg-status-error/5", !nested && !flagged && "bg-paper-warm")}>
      <input
        type="checkbox"
        className="h-4 w-4 shrink-0 cursor-pointer accent-gold"
        checked={selected}
        aria-label={`Select ${item.title}`}
        onChange={(e) => onToggleSelect(item.itemId, e.target.checked)}
        onClick={(e) => e.stopPropagation()}
      />
      <button type="button" onClick={onOpen} className="flex min-w-0 flex-1 flex-col gap-space-1 text-left">
        <div className="flex flex-wrap items-center gap-space-2">
          <span className="rounded-sm bg-white-warm px-space-1 text-caption font-semibold uppercase tracking-wide text-gray-mid">{fileTypeTag(item)}</span>
          <span className="truncate font-semibold text-onyx">{item.title}</span>
        </div>
        {item.kind === "Document" && item.originalFileName && <span className="truncate text-caption text-gray-mid">{item.originalFileName}</span>}
      </button>
      <div className="flex flex-wrap items-center justify-end gap-space-1"><RowBadges item={item} /></div>
      {flagged
        ? <Button size="sm" onClick={onOpen}>Review</Button>
        : <Button size="sm" variant="secondary" className="opacity-0 transition-opacity group-hover:opacity-100 focus-visible:opacity-100" onClick={onOpen}>Review</Button>}
    </div>
  );
}

// Deal-scoped wrapper over the shared paginated picker: the deal's own folder
// tree plus a server-paginated "Search deal folders…" box that browses by
// default (25 + Load more, no typing required).
function FolderPicker({ dealId, value, onChange }: { dealId: string | null; value: string | null; onChange: (id: string) => void }) {
  return (
    <SharedFolderPicker
      value={value}
      onChange={onChange}
      treeSource={dealId ? { kind: "entity", entityType: "Deal", entityId: dealId } : null}
      search={{ placeholder: "Search deal folders…", category: "Deal" }}
      browseByDefault
    />
  );
}

function BulkApproveModal({ submissionId, dealId, items, onClose, onDone, refresh }: { submissionId: string; dealId: string | null; items: DealStagingReviewItem[]; onClose: () => void; onDone: () => void; refresh: () => void }) {
  const { toast } = useToast();
  const [folderId, setFolderId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  async function run() {
    if (!folderId) return;
    setBusy(true); setErr(null);
    const failures: string[] = [];
    for (const it of items) {
      try { await dealStagingApi.approveAndFileItem(submissionId, { itemId: it.itemId, folderId }); }
      catch (error) { failures.push(`${it.title}: ${extractErrorMessage(error)}`); }
    }
    setBusy(false);
    refresh(); // reflect whatever succeeded; a retry then only hits the remaining items
    if (failures.length === 0) {
      toast({ tone: "success", message: `${items.length} item${items.length === 1 ? "" : "s"} approved and filed.` });
      onDone();
    } else {
      const message = `${failures.length} of ${items.length} item${items.length === 1 ? "" : "s"} could not be filed: ${failures.join(" • ")}`;
      setErr(message);
      toast({ tone: "error", message });
    }
  }
  return (
    <Modal open onClose={onClose} size="md" title={`Approve & file ${items.length} item${items.length === 1 ? "" : "s"}`}
      footer={<><Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button><Button disabled={!folderId || busy} loading={busy} onClick={run}>Approve & file</Button></>}>
      <div className="flex flex-col gap-space-3">
        <p className="text-small text-gray-mid m-0">Each selected item will be approved and filed into the folder you choose.</p>
        {err && <p className="text-small text-status-error m-0">{err}</p>}
        <FolderPicker dealId={dealId} value={folderId} onChange={setFolderId} />
      </div>
    </Modal>
  );
}

function BulkFlagModal({ submissionId, items, copy, onClose, onDone, refresh }: { submissionId: string; items: DealStagingReviewItem[]; copy: StagingOriginCopy; onClose: () => void; onDone: () => void; refresh: () => void }) {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  async function run() {
    if (!note.trim()) return;
    setBusy(true); setErr(null);
    let failed = 0;
    for (const it of items) {
      try { await dealStagingApi.returnReviewItem(submissionId, { itemId: it.itemId, body: note.trim() }); }
      catch { failed++; }
    }
    setBusy(false);
    refresh();
    if (failed === 0) onDone();
    else setErr(`${failed} of ${items.length} item${items.length === 1 ? "" : "s"} could not be returned — refreshed; retry the rest.`);
  }
  return (
    <Modal open onClose={onClose} size="sm" title={`Request changes to ${items.length} item${items.length === 1 ? "" : "s"}`}
      footer={<><Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button><Button variant="destructive" disabled={!note.trim() || busy} loading={busy} onClick={run}>Request changes</Button></>}>
      <div className="flex flex-col gap-space-3">
        <label className="text-small font-semibold text-onyx">{copy.facingNoteLabel}</label>
        <Textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} />
        <p className="text-caption text-gray-mid m-0">Attached to each flagged item. {copy.notifiedOnReturnHint}</p>
        {err && <p className="text-small text-status-error m-0">{err}</p>}
      </div>
    </Modal>
  );
}

interface ReviewItemDrawerProps {
  submission: DealStagingDetail;
  item: DealStagingReviewItem;
  /** Threaded from the page so the drawer's counterparty copy and the
   *  liaison-supplied note both read the same resolved origin. */
  origin: DealSubmissionOrigin;
  copy: StagingOriginCopy;
  mutable: boolean;
  // Wider than `mutable`: also true while Approved, so the reviewer can correct
  // a field that fails Create Deal validation. Gates ONLY the section editor.
  correctable: boolean;
  returnable: boolean;
  canOverrideScan: boolean;
  canReview: boolean;
  onClose: () => void;
  onSelectItem: (itemId: string) => void;
  onSaved: () => void;
  onSaveSectionEdit: (sectionId: string, savedDataJson: string) => Promise<void>;
  onOverrideScan: (documentId: string, reason: string) => Promise<void>;
}

function ReviewItemDrawer({ submission, item, origin, copy, mutable, correctable, returnable, canOverrideScan, canReview, onClose, onSelectItem, onSaved, onSaveSectionEdit, onOverrideScan }: ReviewItemDrawerProps) {
  const [folderId, setFolderId] = useState<string | null>(item.reviewFiledFolderId);
  const [returnMode, setReturnMode] = useState(false);
  const [returnNote, setReturnNote] = useState("");
  const section = submission.sections.find((s) => s.sectionId === item.sectionId) ?? null;
  const doc = item.documentId ? submission.documents.find((d) => d.documentId === item.documentId) ?? null : null;
  const index = submission.reviewItems.findIndex((i) => i.itemId === item.itemId);
  const previous = index > 0 ? submission.reviewItems[index - 1] : null;
  const next = index >= 0 && index < submission.reviewItems.length - 1 ? submission.reviewItems[index + 1] : null;

  const { toast } = useToast();
  const [filingMessage, setFilingMessage] = useState<string | null>(null);
  const [filingError, setFilingError] = useState<string | null>(null);

  const approveItem = useMutation({
    mutationFn: () => dealStagingApi.approveReviewItem(submission.submission.id, { itemId: item.itemId }),
    onSuccess: () => {
      const message = `${item.title} approved.`;
      setFilingMessage(message);
      toast({ tone: "success", message });
      onSaved();
    },
    onError: (err) => {
      const message = extractErrorMessage(err);
      setFilingError(message);
      toast({ tone: "error", message });
    },
  });
  const fileItem = useMutation({
    mutationFn: () => dealStagingApi.fileReviewItem(submission.submission.id, { itemId: item.itemId, folderId: folderId! }),
    onMutate: () => {
      setFilingError(null);
      setFilingMessage(null);
    },
    onSuccess: () => {
      const message = `${item.title} filed.`;
      setFilingMessage(message);
      toast({ tone: "success", message });
      onSaved();
    },
    onError: (err) => {
      const message = extractErrorMessage(err);
      setFilingError(message);
      toast({ tone: "error", message });
    },
  });
  const returnItem = useMutation({
    mutationFn: () => dealStagingApi.returnReviewItem(submission.submission.id, { itemId: item.itemId, body: returnNote.trim() }),
    onSuccess: () => { setReturnMode(false); setReturnNote(""); onSaved(); },
  });

  // Staging recovery — manual recovery for stuck async jobs. State reflects the
  // server's actual response after the action; toasts mirror the real outcome.
  const regenerateAssessment = useMutation({
    mutationFn: () => dealStagingApi.regenerateAssessment(submission.submission.id, section!.sectionStateId),
    onSuccess: () => { toast({ tone: "success", message: "Assessment regeneration started." }); onSaved(); },
    onError: (err) => toast({ tone: "error", message: extractErrorMessage(err) }),
  });
  const refreshScan = useMutation({
    mutationFn: () => dealStagingApi.refreshScan(submission.submission.id, doc!.documentId),
    onSuccess: () => { toast({ tone: "success", message: "Scan refresh started." }); onSaved(); },
    onError: (err) => toast({ tone: "error", message: extractErrorMessage(err) }),
  });
  // Document items have no per-document assessment status model; their recovery
  // is re-rendering the persisted markdown to the branded PDF (the same action
  // the doc panel uses), which unsticks an assessment whose PDF never rendered.
  const regenerateDocAssessment = useMutation({
    mutationFn: () => dealStagingApi.generateDocumentAssessment(submission.submission.id, doc!.documentId),
    onSuccess: () => { toast({ tone: "success", message: "Assessment regeneration started." }); onSaved(); },
    onError: (err) => toast({ tone: "error", message: extractErrorMessage(err) }),
  });

  // Bug 3 recovery for a stuck form whose answer document never generated:
  // re-render the section-answers PDF (so File works), or bypass/skip filing.
  const [bypassOpen, setBypassOpen] = useState(false);
  const regenerateAnswers = useMutation({
    mutationFn: () => dealStagingApi.generateSectionAnswers(submission.submission.id, item.sectionId),
    onSuccess: () => { toast({ tone: "success", message: "Answer document regenerated." }); onSaved(); },
    onError: (err) => toast({ tone: "error", message: extractErrorMessage(err) }),
  });
  const bypassItem = useMutation({
    mutationFn: () => dealStagingApi.bypassReviewItem(submission.submission.id, { itemId: item.itemId }),
    onSuccess: () => { setBypassOpen(false); toast({ tone: "success", message: `${item.title} skipped.` }); onSaved(); },
    onError: (err) => toast({ tone: "error", message: extractErrorMessage(err) }),
  });

  const sectionAnswersUrl = useQuery({ queryKey: ["deal-staging-section-answers", submission.submission.id, item.sectionId, "pdf"], queryFn: () => dealStagingApi.sectionAnswersDownloadUrl(submission.submission.id, item.sectionId, "pdf"), enabled: item.kind === "Form" && Boolean(section?.hasSectionAnswersPdf) });
  const sectionAssessmentUrl = useQuery({ queryKey: ["deal-staging-section-assessment", submission.submission.id, item.sectionId, "pdf"], queryFn: () => dealStagingApi.sectionAssessmentDownloadUrl(submission.submission.id, item.sectionId, "pdf"), enabled: item.kind === "Form" && Boolean(section?.hasAssessmentPdf) });
  const documentUrl = useQuery({ queryKey: ["deal-staging-document", submission.submission.id, item.documentId], queryFn: () => dealStagingApi.documentDownloadUrl(submission.submission.id, item.documentId!), enabled: item.kind === "Document" && Boolean(item.documentId) });
  const documentAssessmentUrl = useQuery({ queryKey: ["deal-staging-document-assessment", submission.submission.id, item.documentId, "pdf"], queryFn: () => dealStagingApi.documentAssessmentDownloadUrl(submission.submission.id, item.documentId!, "pdf"), enabled: item.kind === "Document" && Boolean(item.documentId && doc?.hasAssessmentPdf) });

  const left: PreviewDocument = item.kind === "Form"
    ? { title: "Form answers", fileName: "section-answers.pdf", contentType: "application/pdf", url: sectionAnswersUrl.data?.url ?? null, state: sectionAnswersUrl.data?.url ? "ready" : "unavailable", message: "Generated form answers PDF is not available yet.", onDownload: sectionAnswersUrl.data?.url ? () => window.open(sectionAnswersUrl.data.url, "_blank", "noopener,noreferrer") : undefined }
    : { title: "Uploaded document", fileName: doc?.originalFileName ?? item.title, contentType: doc?.contentType ?? "application/octet-stream", url: documentUrl.data?.previewUrl ?? documentUrl.data?.url ?? null, isPdf: documentUrl.data?.isPdf, state: documentUrl.data?.url ? "ready" : "unavailable", message: "Uploaded document is not available yet.", onDownload: documentUrl.data?.url ? () => window.open(documentUrl.data.url, "_blank", "noopener,noreferrer") : undefined };
  const right: PreviewDocument = item.kind === "Form"
    ? {
        title: "Form AI assessment",
        fileName: "section-assessment.pdf",
        contentType: "application/pdf",
        url: sectionAssessmentUrl.data?.url ?? null,
        // Drive the preview state purely from the PERSISTED assessment status —
        // never from markdown presence — so the badge can't disagree with the
        // API. A Pending run that was never started (no start stamp) reads as an
        // explicit "Not generated", distinct from an in-progress "Generating…".
        state: section?.assessmentStatus === "Completed"
          ? section?.hasAssessmentPdf && sectionAssessmentUrl.data?.url
            ? "ready"
            : "loading"
          : section?.assessmentStatus === "Failed"
            ? "failed"
            : section?.assessmentStatus === "Pending" && section?.assessmentStartedAt
              ? "generating"
              : "notgenerated",
        message: section?.assessmentStatus === "Failed"
          ? section?.assessmentError ?? "AI assessment generation failed. Use Regenerate to try again."
          : section?.assessmentStatus === "Skipped"
            ? "No AI assessment is configured for this section."
            : section?.assessmentStatus === "Pending" && section?.assessmentStartedAt
              ? undefined
              : "AI assessment has not been generated yet. Run Evaluate & Feedback to generate it.",
        generatingSince: section?.assessmentStartedAt ?? null,
        onRegenerate: canReview && mutable && section?.sectionStateId
          ? () => regenerateAssessment.mutate()
          : undefined,
        regenerateDisabled: regenerateAssessment.isPending,
        onDownload: sectionAssessmentUrl.data?.url ? () => window.open(sectionAssessmentUrl.data.url, "_blank", "noopener,noreferrer") : undefined,
      }
    : {
        title: "Document AI assessment",
        fileName: "document-assessment.pdf",
        contentType: "application/pdf",
        url: documentAssessmentUrl.data?.url ?? null,
        state: doc?.hasAssessmentPdf && documentAssessmentUrl.data?.url ? "ready" : doc?.aiAssessmentMarkdown ? "generating" : "unavailable",
        message: "Generated document AI assessment is not available yet.",
        // Regenerate re-renders the persisted markdown; only meaningful once the
        // doc has an assessment (a stuck render), so gate on markdown presence.
        onRegenerate: canReview && mutable && doc?.aiAssessmentMarkdown
          ? () => regenerateDocAssessment.mutate()
          : undefined,
        regenerateDisabled: regenerateDocAssessment.isPending,
        onDownload: documentAssessmentUrl.data?.url ? () => window.open(documentAssessmentUrl.data.url, "_blank", "noopener,noreferrer") : undefined,
      };

  // A form can only be filed once its answer document (section-answers PDF) has
  // generated. When it hasn't, block File and offer Regenerate / Bypass instead.
  const formAnswersMissing = item.kind === "Form" && !section?.hasSectionAnswersPdf;

  return (
    <ResourceDrawer open onClose={onClose} width="xl" title={item.title} subtitle={`${item.kind} item in ${item.sectionTitle}`} testId="deal-staging-review-item-drawer">
      <div className="flex flex-col gap-space-4">
        <div className="flex flex-wrap items-center justify-between gap-space-2"><div className="flex flex-wrap items-center gap-space-2"><SectionReviewBadge status={item.reviewStatus} />{item.isBypassed ? <Badge status="archived">Skipped</Badge> : item.isFiled && <Badge status="finalized">Filed</Badge>}{item.scanStatus && <ScanBadge status={item.scanStatus} />}</div><div className="flex gap-space-2"><Button variant="secondary" size="sm" disabled={!previous} onClick={() => previous && onSelectItem(previous.itemId)}><ChevronLeft size={14} /> Previous</Button><Button variant="secondary" size="sm" disabled={!next} onClick={() => next && onSelectItem(next.itemId)}>Next <ChevronRight size={14} /></Button></div></div>
        <DocumentComparisonPanel left={left} right={right} />
        {right.state === "generating" && <p className="text-caption text-gray-mid m-0">AI assessment is still generating. Approve & file will file the primary item now and skip the assessment until it is available.</p>}
        {filingMessage && <p className="text-small text-status-success m-0" role="status">{filingMessage}</p>}
        {filingError && <p className="text-small text-status-error m-0" role="alert">{filingError}</p>}
        {item.kind === "Form" && section && (
          <>
            {correctable && !mutable && (
              <p className="text-caption text-gray-mid m-0">Correcting an approved submission re-generates this section's answers.</p>
            )}
            <SectionEditor section={section} disabled={!correctable} origin={origin} onSave={(json) => onSaveSectionEdit(section.sectionId, json)} />
          </>
        )}
        {formAnswersMissing && mutable && !item.isFiled && (
          <Card className="p-space-3 flex flex-col gap-space-2">
            <p className="text-caption text-status-warning m-0">This form's answer document hasn't generated yet, so it can't be filed. Regenerate it, or bypass to move past this item.</p>
            <div className="flex justify-end gap-space-2">
              {canReview && <Button variant="secondary" size="sm" loading={regenerateAnswers.isPending} onClick={() => regenerateAnswers.mutate()} data-testid="deal-staging-regenerate-answers">Regenerate</Button>}
              <Button variant="secondary" size="sm" disabled={bypassItem.isPending} onClick={() => setBypassOpen(true)} data-testid="deal-staging-bypass-item">Bypass</Button>
            </div>
          </Card>
        )}
        <section className="rounded-md border border-gray-light p-space-4 flex flex-col gap-space-3"><h3 className="text-small font-semibold text-onyx">File into folder</h3><FolderPicker dealId={submission.submission.importedDealId} value={folderId} onChange={setFolderId} /></section>
        {!returnMode ? <div className="flex justify-end gap-space-2"><Button variant="secondary" disabled={!returnable} onClick={() => setReturnMode(true)}><RotateCcw size={14} className="mr-1 inline" /> Request changes</Button><Button disabled={!mutable || (item.reviewStatus !== "Approved" && approveItem.isPending)} loading={approveItem.isPending} onClick={() => { if (item.reviewStatus !== "Approved") approveItem.mutate(); }} className={item.reviewStatus === "Approved" ? "bg-status-success text-white-pure hover:bg-status-success" : undefined}>{item.reviewStatus === "Approved" ? "Approved" : "Approve"}</Button><Button disabled={!mutable || (!item.isFiled && (!folderId || fileItem.isPending || (item.kind === "Document" && item.scanStatus !== "Clean") || formAnswersMissing))} loading={fileItem.isPending} onClick={() => { if (!item.isFiled) fileItem.mutate(); }} className={item.isFiled && !item.isBypassed ? "bg-status-success text-white-pure hover:bg-status-success" : undefined}>{item.isBypassed ? "Skipped" : item.isFiled ? "Filed" : "File"}</Button></div> : <Card className="p-space-4 flex flex-col gap-space-3"><label className="text-small font-semibold text-onyx">{copy.facingNoteItemLabel}</label><Textarea rows={3} value={returnNote} onChange={(e) => setReturnNote(e.target.value)} /><p className="text-caption text-gray-mid m-0">Flags this item for changes. {copy.notifiedOnReturnHint}</p><div className="flex justify-end gap-space-2"><Button variant="secondary" onClick={() => setReturnMode(false)}>Cancel</Button><Button variant="destructive" disabled={!returnNote.trim()} loading={returnItem.isPending} onClick={() => returnItem.mutate()}>Request changes</Button></div></Card>}
        {doc && mutable && (
          <ScanRecoveryControls
            scanStatus={doc.scanStatus}
            scanError={doc.scanError}
            canRefresh={canReview}
            onRefresh={() => refreshScan.mutate()}
            refreshing={refreshScan.isPending}
          />
        )}
        {canOverrideScan && doc && doc.scanStatus !== "Clean" && doc.scanStatus !== "Infected" && <ScanOverride doc={doc} onOverride={onOverrideScan} />}
      </div>
      <ConfirmModal
        open={bypassOpen}
        title="Bypass this form?"
        message="The form's answer document can't be generated. Bypassing marks it resolved and skips filing so you can create the deal. It will show as “Skipped”, not filed."
        confirmLabel="Bypass"
        onConfirm={() => bypassItem.mutate()}
        onCancel={() => setBypassOpen(false)}
      />
    </ResourceDrawer>
  );
}

function ScanOverride({ doc, onOverride }: { doc: DealStagingDocument; onOverride: (documentId: string, reason: string) => Promise<void> }) {
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  return <Card className="p-space-4 flex flex-col gap-space-2"><h3 className="text-h4">Scan override for {doc.originalFileName}</h3><Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason for override (recorded in audit)" /><div className="flex justify-end"><Button size="sm" disabled={!reason.trim()} loading={saving} onClick={async () => { setSaving(true); try { await onOverride(doc.documentId, reason.trim()); setReason(""); } finally { setSaving(false); } }}>Override scan</Button></div></Card>;
}
